import { usePermissionMonitor } from "#components/PermissionMonitor/index.js";
import CloseIcon from "@mui/icons-material/Close";
import { FormControlLabel, Tooltip } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import IndeterminateIcon from "components/IndeterminateIcon";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Dialog from "components/NoScrollDialog";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import auditsService from "services/audits-service";
import AssessmentReportDialog from "./AssessmentReportDialog/index.js";
import MysteryAssessmentReportDialog from "./MysteryAssessmentReportDialog";

export default function AuditGenerateReportDialog({
  isOpen,
  audit,
  contacts,
  fetchContacts,
  handleEditAuditInfo,
  handleEditAuditFirstAnsweredAt,
  onClose,
}) {
  const { label } = useLanguage();
  const [loading, setLoading] = React.useState({
    update: false,
  });
  const [open, setOpen] = React.useState(false);
  const [aReportDialogOpen, setAReportDialogOpen] = useState(false);
  const [auditTime, setAuditTime] = useState(audit.firstAnsweredAt);
  const [selectedSummaryGroups, setSelectedSummaryGroups] = useState({});
  const [branchData, setBranchData] = useState(null);
  const [branchDataError, setBranchDataError] = useState(false);
  const loadedBranchData = audit.branchDataLoaded === false &&
    branchData?.auditId === audit.id && branchData?.version === audit.updatedAt
      ? branchData.data : null;
  const branchDataReady = audit.branchDataLoaded !== false || Boolean(loadedBranchData);
  const can = usePermissionMonitor();

  React.useEffect(() => {
    if (!isOpen || audit.branchDataLoaded !== false) return;
    let active = true;
    setBranchData(null);
    setBranchDataError(false);
    setLoading((current) => ({ ...current, branchData: true }));
    auditsService.getAuditBranchData(audit.id).then(([response, data]) => {
      if (!active) return;
      if (response.ok && data) {
        setBranchData({ auditId: audit.id, version: audit.updatedAt, data });
      } else {
        setBranchDataError(true);
      }
    }).catch(() => {
      if (active) setBranchDataError(true);
    }).finally(() => {
      if (active) setLoading((current) => ({ ...current, branchData: false }));
    });
    return () => { active = false; };
  }, [isOpen, audit.id, audit.updatedAt, audit.branchDataLoaded]);

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  const summaryGroups = useMemo(() => {
    const sumGrps = {};
    audit.pillars.forEach((pillar) => {
      if (pillar?.auditPillar?.summaryGroupId) {
        if (sumGrps[pillar.auditPillar.summaryGroupId]) {
          sumGrps[pillar.auditPillar.summaryGroupId].pillarsCount++;
        } else {
          sumGrps[pillar.auditPillar.summaryGroupId] = {
            ...pillar.auditPillar.summaryGroup,
            pillarsCount: 1,
          };
        }
      } else {
        if (sumGrps["none"]) {
          sumGrps["none"].pillarsCount++;
        } else {
          sumGrps["none"] = {
            pillarsCount: 1,
          };
        }
      }
    });

    return Object.values(sumGrps);
  }, [audit]);

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setSelectedSummaryGroups(() => {
      const newValue = {};

      summaryGroups.forEach((summaryGroup) => {
        newValue[summaryGroup.id] = true;
      });

      return newValue;
    });
  }, [isOpen, summaryGroups]);

  React.useEffect(() => {
    setAuditTime(audit.firstAnsweredAt);
  }, [audit.firstAnsweredAt]);

  React.useEffect(() => {
    if (!isOpen || !audit) return;
    if (audit.auditTypeId != 2 && !Array.isArray(contacts)) fetchContacts();
  }, [isOpen, audit, contacts, fetchContacts]);

  const filteredAudit = React.useMemo(() => {
    const summaryGroupIds = Object.keys(selectedSummaryGroups).filter(
      (k) => selectedSummaryGroups[k],
    );

    if (summaryGroupIds.length == 0) {
      return { ...audit, ...loadedBranchData, pillars: [], competencies: [] };
    }

    const pillars = audit.pillars.filter((pillar) => {
      return pillar?.auditPillar?.summaryGroupId
        ? summaryGroupIds.includes(`${pillar?.auditPillar?.summaryGroupId}`)
        : summaryGroupIds.includes("undefined");
    });

    const selectedKpiIds = new Set(
      pillars.flatMap((pillar) =>
        pillar.kpis.map(
          (kpi) => `${kpi.id ?? kpi.auditKpis?.[0]?.id ?? kpi.referenceCode}`,
        ),
      ),
    );

    const competencies =
      audit.competencies?.reduce((filteredCompetencies, competency) => {
        const kpis =
          competency.kpis?.filter((kpi) =>
            selectedKpiIds.has(
              `${kpi.id ?? kpi.auditKpis?.[0]?.id ?? kpi.referenceCode}`,
            ),
          ) || [];

        if (kpis.length) {
          filteredCompetencies.push({
            ...competency,
            kpis,
          });
        }

        return filteredCompetencies;
      }, []) || [];

    return { ...audit, ...loadedBranchData, pillars, competencies };
  }, [audit, selectedSummaryGroups, loadedBranchData]);

  return (
    <>
      {branchDataReady && (audit.auditTypeId != 2 ? (
        <AssessmentReportDialog
          summaryGroups={selectedSummaryGroups}
          isOpen={aReportDialogOpen}
          audit={filteredAudit}
          contacts={contacts}
          handleEditAuditInfo={handleEditAuditInfo}
        />
      ) : (
        <MysteryAssessmentReportDialog
          summaryGroups={selectedSummaryGroups}
          isOpen={aReportDialogOpen}
          audit={filteredAudit}
          contacts={contacts}
          handleEditAuditInfo={handleEditAuditInfo}
        />
      ))}
      <Dialog fullWidth={true} maxWidth={"md"} open={open}>
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <MDTypography variant="h5">
              {label("generate-audit-report-button")}
            </MDTypography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          {branchDataError && (
            <MDTypography color="error" variant="body2">
              Could not load report history and tasks. Close and reopen this dialog to retry.
            </MDTypography>
          )}
          <Tooltip
            title={can(
              "updateSiteIn",
              "audit",
              null,
              () =>
                "You don't have the necessary permissions to update the audit time",
              () => null,
            )}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <MDBox
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap={1}
                sx={{
                  paddingTop: "10px",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    disabled={!can("updateSiteIn", "audit")}
                    label={label("audit-time")}
                    required={true}
                    defaultValue={dayjs(auditTime)}
                    onChange={(newValue) => {
                      setAuditTime(new Date(newValue.toDate()));
                    }}
                    format="DD/MM/YYYY hh:mm A"
                  />
                </LocalizationProvider>
                <MDTypography variant="caption">
                  {label("change-audit-time-in-report")}
                </MDTypography>
              </MDBox>
              <LoadingButton
                disabled={
                  !can("updateSiteIn", "audit") ||
                  !auditTime ||
                  !dayjs(auditTime).isValid()
                }
                loading={loading.update}
                type="button"
                variant="gradient"
                color="info"
                onClick={async () => {
                  setLoading({ ...loading, update: true });
                  await handleEditAuditFirstAnsweredAt({
                    date: auditTime,
                  });
                  setLoading({ ...loading, update: false });
                }}
              >
                {label("save-button")}
              </LoadingButton>
            </MDBox>
          </Tooltip>

          <Divider variant="fullWidth" />
          <MDBox
            display="flex"
            justifyContent="space-between"
            gap={2}
            alignItems="start"
          >
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap={1}
            >
              <MDTypography variant="h6">
                {label("assessment-report")}
              </MDTypography>
              <MDTypography variant="caption">
                {label("select-required-summary-groups")}
              </MDTypography>

              <FormControlLabel
                label={label("select-all")}
                control={
                  <Checkbox
                    checked={Boolean(
                      Object.values(selectedSummaryGroups).filter((v) => v)
                        .length == summaryGroups?.length,
                    )}
                    indeterminate={Boolean(
                      Object.values(selectedSummaryGroups).filter((v) => v)
                        .length != summaryGroups?.length &&
                        Object.values(selectedSummaryGroups).filter((v) => v)
                          .length != 0,
                    )}
                    indeterminateIcon={<IndeterminateIcon />}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setSelectedSummaryGroups((curr) => {
                        const newValue = { ...curr };

                        summaryGroups.forEach((summaryGroup) => {
                          newValue[summaryGroup.id] = checked;
                        });

                        return newValue;
                      });
                    }}
                  />
                }
              />
              <MDBox
                display="flex"
                flexDirection="column"
                gap={0}
                sx={{ paddingLeft: 2 }}
              >
                {summaryGroups?.map((summaryGroup) => (
                  <FormControlLabel
                    sx={{ padding: 0, margin: 0 }}
                    key={summaryGroup.id}
                    label={`${
                      summaryGroup.description || label("no-summary-groups")
                    } (${summaryGroup.pillarsCount} ${
                      summaryGroup.pillarsCount == 1
                        ? label("pillar-singular")
                        : label("pillar-plural")
                    })`}
                    control={
                      <Checkbox
                        checked={Boolean(
                          selectedSummaryGroups[summaryGroup.id],
                        )}
                        indeterminate={false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setSelectedSummaryGroups((curr) => {
                              const newValue = { ...curr };
                              newValue[summaryGroup.id] = true;
                              return newValue;
                            });
                          } else {
                            setSelectedSummaryGroups((curr) => {
                              const newValue = { ...curr };
                              newValue[summaryGroup.id] = false;
                              return newValue;
                            });
                          }
                        }}
                      />
                    }
                  />
                ))}
              </MDBox>
            </MDBox>
            <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
              <LoadingButton
                loading={Boolean(loading.branchData)}
                disabled={!branchDataReady}
                type="button"
                variant="gradient"
                color="info"
                onClick={() => setAReportDialogOpen(Math.random())}
              >
                {label("generate-button")}
              </LoadingButton>

              {/* <LoadingButton
                loading={generatingLink}
                type="button"
                variant="gradient"
                color="primary"
                onClick={() => {
                  generateLink();
                }}
              >
                Generate link
              </LoadingButton>*/}
            </div>
          </MDBox>
        </DialogContent>
      </Dialog>
    </>
  );
}
